import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Building2, MapPin, Clock, Mail, Phone, ArrowLeft, Sparkles, Calendar, ShieldCheck } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>About Us | Scratch Unleashed - Operated by Charms Hospitality Services</title>
        <meta
          name="description"
          content="Scratch Unleashed is Jamshedpur's premier nightclub, operated by Charms Hospitality Services from Bindal Mall, Sonari. World-class DJs, VIP tables, private events."
        />
        <meta name="keywords" content="About Scratch Unleashed, Charms Hospitality Services, nightclub Jamshedpur, Bindal Mall Sonari, hospitality Jharkhand" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://scratchjsr.com/about" />
        <meta property="og:title" content="About Scratch Unleashed — Operated by Charms Hospitality Services" />
        <meta
          property="og:description"
          content="Jamshedpur's premier nightclub at Bindal Mall, Sonari. Operated by Charms Hospitality Services. World-class DJs, VIP tables, private events."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://scratchjsr.com/about" />
      </Helmet>

      <div data-testid="about-page" className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="fixed top-6 left-6 z-50">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            data-testid="about-back-button"
            className="gap-2 text-[#B9B9B9] hover:text-[#F2F2F2]"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </div>

        {/* Hero */}
        <section className="relative pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
              style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.25)' }}
            >
              <Building2 size={32} style={{ color: '#D4A843' }} />
            </div>
            <h1 className="font-['EB_Garamond'] text-5xl md:text-6xl font-bold text-[#F2F2F2] mb-4">
              About Us
            </h1>
            <p className="text-lg text-[#B9B9B9] max-w-2xl mx-auto">
              Jamshedpur's premier nightlife destination — where music, mood and memory meet on every weekend.
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="pb-20 px-4">
          <div className="max-w-4xl mx-auto space-y-12">

            {/* The Story */}
            <div
              className="p-8 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
              data-testid="about-story"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                  <Sparkles size={20} style={{ color: '#D4A843' }} />
                </div>
                <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                  The Story
                </h2>
              </div>
              <div className="space-y-4 text-[#B9B9B9] leading-relaxed">
                <p>
                  Scratch Unleashed was created to give Jamshedpur a nightlife experience that doesn't make you envy bigger cities. We blend
                  world-class DJ programming, a thoughtfully designed lounge and dance floor, premium bar service, and a kitchen that takes the
                  late-night menu seriously.
                </p>
                <p>
                  Located on the 3rd floor of Bindal Mall in Sonari, we operate Friday through Sunday with the doors opening at 6 PM and the night
                  running late. Weekdays are reserved for private events, corporate nights, and bespoke celebrations.
                </p>
              </div>
            </div>

            {/* Legal Entity */}
            <div
              className="p-8 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
              data-testid="about-legal"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                  <ShieldCheck size={20} style={{ color: '#D4A843' }} />
                </div>
                <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                  Legal &amp; Business Details
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 text-[#B9B9B9]">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#7A7A72] mb-1">Legal Entity</div>
                  <div className="text-[#F2F2F2] font-semibold">Vishal Sinha (Charms Hospitality Services)</div>
                  <div className="text-xs text-[#7A7A72] mt-1">Sole Proprietorship</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#7A7A72] mb-1">Trading As</div>
                  <div className="text-[#F2F2F2] font-semibold">Scratch Unleashed</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#7A7A72] mb-1">Industry</div>
                  <div className="text-[#F2F2F2]">Hospitality &amp; Entertainment — Drinking Place / Nightclub (MCC 5813)</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#7A7A72] mb-1">Operating Since</div>
                  <div className="text-[#F2F2F2]">2024</div>
                </div>
              </div>
            </div>

            {/* Visit / Hours */}
            <div
              className="grid md:grid-cols-2 gap-6"
            >
              <div
                className="p-8 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                data-testid="about-visit"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                    <MapPin size={20} style={{ color: '#D4A843' }} />
                  </div>
                  <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                    Visit Us
                  </h2>
                </div>
                <div className="text-[#B9B9B9] leading-relaxed">
                  3rd Floor, Bindal Mall<br />
                  Sonari, Jamshedpur<br />
                  Jharkhand 831011, India
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Scratch+Unleashed+Bindal+Mall+Sonari+Jamshedpur"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-[#D4A843] hover:text-[#F1E2B8] transition-colors text-sm"
                >
                  Get directions on Google Maps →
                </a>
              </div>

              <div
                className="p-8 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                data-testid="about-hours"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                    <Clock size={20} style={{ color: '#D4A843' }} />
                  </div>
                  <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                    Hours
                  </h2>
                </div>
                <div className="text-[#B9B9B9] leading-relaxed">
                  Friday – Sunday<br />
                  6:00 PM – Late<br />
                  <span className="text-sm text-[#7A7A72]">Weekdays: private events &amp; reservations</span>
                </div>
              </div>
            </div>

            {/* What We Do */}
            <div
              className="p-8 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
              data-testid="about-offerings"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                  <Calendar size={20} style={{ color: '#D4A843' }} />
                </div>
                <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                  What We Offer
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-[#B9B9B9]">
                <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.07)]">
                  <div className="text-[#F2F2F2] font-semibold mb-1">VIP Table Booking</div>
                  <p className="text-sm">Bottle service and reserved seating for groups of 4 to 20 guests.</p>
                </div>
                <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.07)]">
                  <div className="text-[#F2F2F2] font-semibold mb-1">Private Events</div>
                  <p className="text-sm">Birthday parties, corporate nights, and bespoke celebrations on weekdays.</p>
                </div>
                <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.07)]">
                  <div className="text-[#F2F2F2] font-semibold mb-1">DJ Nights</div>
                  <p className="text-sm">World-class artists every Friday, Saturday and Sunday.</p>
                </div>
                <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.07)]">
                  <div className="text-[#F2F2F2] font-semibold mb-1">Premium Bar &amp; Kitchen</div>
                  <p className="text-sm">Curated cocktail menu, Indian, Continental and Asian Fusion plates.</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="mailto:admin@scratchjsr.com"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#D4A843] transition-all text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid="about-email-cta"
              >
                <Mail size={24} className="mx-auto mb-3 text-[#D4A843]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">Email Us</h3>
                <p className="text-sm text-[#B9B9B9]">admin@scratchjsr.com</p>
              </a>
              <a
                href="https://wa.me/917870369666"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#25D366] transition-all text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid="about-whatsapp-cta"
              >
                <Phone size={24} className="mx-auto mb-3 text-[#25D366]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">WhatsApp / Phone</h3>
                <p className="text-sm text-[#B9B9B9]">+91 78703 69666</p>
              </a>
            </div>
          </div>
        </section>

        <footer className="border-t border-[rgba(255,255,255,0.07)] py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-sm text-[#6B6B6B]">
              © {new Date().getFullYear()} Charms Hospitality Services. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              <button onClick={() => navigate('/terms-and-conditions')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Terms &amp; Conditions</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/privacy-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Privacy Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/refund-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Refund Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/shipping-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Shipping Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Contact Us</button>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
